import React, { useCallback, useEffect, useState } from 'react';
import { Message } from '@/components/brand/icons/Message';
import useCreateLeads from '@/utils/useCreateLeads';
import Button from './button';

interface FloatingContactButtonProps {
  onAutoTrigger?: () => void;
}

const FloatingContactButton: React.FC<FloatingContactButtonProps> = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const { onSubmit } = useCreateLeads();

  // Show tooltip for 5 seconds, then hide for 5 seconds, repeat
  useEffect(() => {
    if (isModalOpen) return; // Don't show tooltip when modal is open

    const showTooltip = () => {
      setShowTooltip(true);
      // Hide after 5 seconds
      setTimeout(() => {
        setShowTooltip(false);
        // Show again after 5 seconds of hiding
        setTimeout(() => {
          if (!isModalOpen) {
            showTooltip(); // Recursive call to repeat the cycle
          }
        }, 5000);
      }, 5000);
    };

    // Start the cycle
    showTooltip();

    // Cleanup function
    return () => {
      setShowTooltip(false);
    };
  }, [isModalOpen]);

  const handleButtonClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleInputChange = useCallback(
    (
      event:
        | React.FormEvent<HTMLInputElement>
        | React.FormEvent<HTMLTextAreaElement>
    ) => {
      const { name, value } = event.currentTarget;
      setFormValues({ ...formValues, [name]: value });
    },
    [formValues]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      try {
        onSubmit(formValues);
        setFormValues({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setIsModalOpen(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error submitting form:', error);
      }
    },
    [onSubmit, formValues]
  );

  return (
    <>
      {/* Floating Button with tooltip */}
      <div className="fixed bottom-6 left-6 z-50">
        <button
          onClick={handleButtonClick}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-blue via-brown to-orange shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl"
          aria-label="Contact us"
        >
          <Message className="h-6 w-6 text-white" />
        </button>

        {/* Simple text tooltip on the button */}
        {showTooltip && (
          <div className="absolute -top-12 left-0 animate-fade-in-out">
            <div className="text-sm font-medium text-white bg-black/80 px-3 py-1 rounded-lg whitespace-nowrap">
              Click to leave a message for admin
            </div>
          </div>
        )}
      </div>

      {/* Modal - compact on small screens (e.g. 13.3" MacBook) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 overflow-y-auto">
          <div className="relative w-full max-w-[min(24rem,90vw)] max-h-[88vh] overflow-y-auto rounded-xl sm:rounded-2xl border border-sky/50 bg-darker/90 p-4 sm:p-6 shadow-xl my-auto">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute right-3 top-3 sm:right-4 sm:top-4 text-gray-400 hover:text-white p-0.5"
              aria-label="Close modal"
            >
              <svg
                className="h-5 w-5 sm:h-6 sm:w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="mb-3 sm:mb-6 pr-8">
              <h3 className="text-lg sm:text-2xl font-medium text-white leading-tight">
                Get in Touch
              </h3>
              <p className="text-xs sm:text-sm text-gray-300 leading-tight mt-1">
                Leave your details and we&apos;ll get back to you as soon as
                possible.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-2.5 sm:space-y-4">
              <div>
                <label
                  htmlFor="modal-name"
                  className="block text-sm font-light text-white"
                >
                  Name<span className="text-orange">*</span>
                </label>
                <input
                  type="text"
                  id="modal-name"
                  name="name"
                  value={formValues.name}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-lg sm:rounded-xl border border-[#4906D0]/60 bg-darker/20 px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-white placeholder:text-white/70"
                  placeholder="Your name"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="modal-email"
                  className="block text-sm font-light text-white"
                >
                  Email<span className="text-orange">*</span>
                </label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-lg sm:rounded-xl border border-[#4906D0]/60 bg-darker/20 px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-white placeholder:text-white/70"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="modal-phone"
                  className="block text-sm font-light text-white"
                >
                  Phone<span className="text-orange">*</span>
                </label>
                <input
                  type="tel"
                  id="modal-phone"
                  name="phone"
                  value={formValues.phone}
                  onChange={handleInputChange}
                  className="mt-1 w-full rounded-lg sm:rounded-xl border border-[#4906D0]/60 bg-darker/20 px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-white placeholder:text-white/70"
                  placeholder="Your phone number"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="modal-message"
                  className="block text-sm font-light text-white"
                >
                  Message<span className="text-orange">*</span>
                </label>
                <textarea
                  id="modal-message"
                  name="message"
                  value={formValues.message}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 w-full resize-none rounded-lg sm:rounded-xl border border-[#4906D0]/60 bg-darker/20 px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-white placeholder:text-white/70 leading-snug outline-none"
                  placeholder="How can we help you?"
                  required
                />
              </div>

              <div className="flex justify-center pt-2 sm:pt-4">
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue via-brown to-orange px-6 py-2 text-sm sm:px-8 sm:py-2 sm:text-base"
                >
                  Send Message
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingContactButton;
